import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  getSlideSenseConfig,
  type SlideSenseConfigResponse,
  type SlideSensePresenterModes,
  updateSlideSenseConfig,
} from "@/api/admin/slide-sense-config"

import { adminQueryKeys } from "../admin-query-keys"
import { GC_TIME, MEDIUM_STALE_TIME } from "./_shared"

// =============================================================================
// Slide Sense module config — which presenter modes users can start
// =============================================================================

export function useSlideSenseConfigQuery(enabled = true) {
  return useQuery<SlideSenseConfigResponse>({
    queryKey: adminQueryKeys.slideSenseConfig(),
    queryFn: getSlideSenseConfig,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateSlideSenseConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation<SlideSenseConfigResponse, Error, SlideSensePresenterModes>({
    mutationFn: (presenterModes) => updateSlideSenseConfig(presenterModes),
    onSuccess: (data) => {
      // Seed the cache with the server's canonical response so the switches
      // reflect the persisted state immediately without a refetch round-trip.
      queryClient.setQueryData(adminQueryKeys.slideSenseConfig(), data)
      // Also invalidate the user-facing Slide Hub config query so an admin
      // who is presenting in the same session sees the gating update without
      // waiting out its staleTime (#2336 H).
      void queryClient.invalidateQueries({ queryKey: ["slide-sense", "module-config"] })
    },
  })
}
